import React, { useEffect, useMemo } from 'react';

import { usePlatformGetFilterByIDTeam } from '@cloudquery/api-platform';
import { useBaseAuthenticated } from '@cloudquery/base-users';

import { TableState } from '../types';
import { checkSavedFilterEquality } from '../utils/tableState';

export const useSavedFilterManager = (
  tableState: TableState,
  setTableState: React.Dispatch<React.SetStateAction<TableState>>,
) => {
  const { currentMembership } = useBaseAuthenticated();

  // Derive savedFilterId directly from tableState - no need for separate state
  const savedFilterId = tableState?.originalFilter?.id ?? '';

  const { data } = usePlatformGetFilterByIDTeam(currentMembership.team.name, savedFilterId, {
    query: { enabled: !!savedFilterId }, // Only fetch when we have an ID
  });

  // Extract values for consistent dependency tracking (React Compiler compatibility)
  const originalFilterValue = tableState.originalFilter?.value;
  const currentFilter = tableState.filter;

  // Sync filter data from API to state
  useEffect(() => {
    if (!data?.data) {
      return;
    }

    const originalFilterValue = data.data.expression;
    setTableState((prev) => ({
      ...prev,
      filter: prev.filter ?? originalFilterValue,
      originalFilter: {
        id: prev.originalFilter!.id,
        name: data.data.name,
        value: originalFilterValue,
      },
    }));
  }, [data, setTableState]);

  // Derive hasDeviated with useMemo instead of useState + useEffect
  const hasDeviated = useMemo(() => {
    if (!originalFilterValue) {
      return false;
    }

    return !checkSavedFilterEquality(originalFilterValue, currentFilter);
  }, [originalFilterValue, currentFilter]);

  return {
    savedFilterHasDeviated: !!savedFilterId && hasDeviated,
  };
};
